<template>
  <v-select
    class="app-select"
    :class="{ 'app-select--light': lightMode }"
    :style="rootStyle"
    v-bind="bindings"
    :menu-props="menuProps"
    v-on="$listeners"
  >
    <!-- 슬롯 투명 전달 (item/selection 커스텀 등) -->
    <template v-for="(_, name) in $scopedSlots" #[name]="slotData"><slot :name="name" v-bind="slotData"></slot></template>
  </v-select>
</template>

<script>
import Themeable from "vuetify/lib/mixins/themeable";

// v-select 래퍼 — App* 톤. 테마는 Themeable 자동 상속(내부 v-select도 provide로 따라옴), dark/light prop은 오버라이드.
//  모든 v-select prop/이벤트/슬롯 투명 전달. 기본값: dense/outlined/hide-details.
//  드롭다운 메뉴는 오버레이로 렌더되므로 contentClass(app-select__menu)로 전역 스타일 적용.
export default {
  name: "AppSelect",
  mixins: [Themeable],
  inheritAttrs: false,
  props: {
    // 박스 높이(px). CSS 변수로 내부 슬롯에 반영.
    height: { type: [Number, String], default: 28 },
  },
  computed: {
    lightMode() {
      return !this.isDark;
    },
    rootStyle() {
      const h = isNaN(this.height) ? this.height : `${this.height}px`;
      return { "--app-select-h": h };
    },
    bindings() {
      return { dense: true, outlined: true, "hide-details": true, ...this.$attrs };
    },
    menuProps() {
      return {
        offsetY: true,
        contentClass: this.lightMode ? "app-select__menu app-select__menu--light" : "app-select__menu",
      };
    },
  },
};
</script>

<style scoped>
/* ── 인풋 박스 (AppSearchInput 톤). 높이는 height prop(--app-select-h) ── */
.app-select ::v-deep .v-input__slot {
  min-height: var(--app-select-h, 28px) !important;
  background: #23232f !important;
  border-radius: 7px;
}
.app-select ::v-deep .v-select__slot {
  height: var(--app-select-h, 28px);
}
/* 낮은 높이에서 화살표/라벨 수직 중앙 정렬 */
.app-select ::v-deep .v-input__append-inner {
  margin-top: 0 !important;
  align-self: center !important;
}
.app-select ::v-deep .v-label:not(.v-label--active) {
  top: 50% !important;
  transform: translateY(-50%) !important;
}
.app-select ::v-deep fieldset {
  border: 1px solid #2f2f3d !important;
}
.app-select.v-input--is-focused ::v-deep fieldset {
  border-color: rgba(145, 85, 253, 0.55) !important;
}
.app-select ::v-deep .v-select__selection {
  font-size: 12px;
  color: #e6e6ee;
}
/* 낮은 height에서 선택값 수직중앙 정렬 붕괴 방지:
   - dense 기본 상하마진(5px)이 슬롯 높이를 초과해 텍스트가 위로 밀림 → 마진 제거(플렉스 중앙정렬에 맡김)
   - 좁은 폭에서 내부 숨은 input(min-width)이 줄바꿈 유발 → nowrap + input 축소 허용 */
.app-select ::v-deep .v-select__selections {
  flex-wrap: nowrap;
}
.app-select ::v-deep .v-select__selection--comma {
  margin-top: 0;
  margin-bottom: 0;
}
.app-select ::v-deep .v-select__selections input {
  min-width: 0;
  padding: 0;
}
.app-select ::v-deep .v-label {
  font-size: 12px;
  color: #6b6b7d;
}
.app-select ::v-deep input::placeholder {
  font-size: 12px;
  color: #6b6b7d !important;
}
.app-select ::v-deep .v-input__append-inner .v-icon {
  font-size: 18px;
  color: #6b6b7d !important;
}
.app-select.v-input--is-focused ::v-deep .v-input__append-inner .v-icon {
  color: #8f7ddb !important;
}

/* ── light theme ── */
.app-select--light ::v-deep .v-input__slot {
  background: #f4f3f8 !important;
}
.app-select--light ::v-deep fieldset {
  border-color: #e2e0ec !important;
}
.app-select--light.v-input--is-focused ::v-deep .v-input__slot {
  background: #fff !important;
}
.app-select--light.v-input--is-focused ::v-deep fieldset {
  border-color: rgba(124, 77, 255, 0.5) !important;
}
.app-select--light ::v-deep .v-select__selection {
  color: #37324a;
}
.app-select--light ::v-deep .v-label {
  color: #a29cb8;
}
.app-select--light ::v-deep input::placeholder {
  color: #a29cb8 !important;
}
.app-select--light ::v-deep .v-input__append-inner .v-icon {
  color: #a29cb8 !important;
}
.app-select--light.v-input--is-focused ::v-deep .v-input__append-inner .v-icon {
  color: #7c4dff !important;
}
</style>

<style>
/* 드롭다운 메뉴(오버레이 렌더 → 전역). 다크 기본 */
.app-select__menu {
  border-radius: 8px !important;
  border: 1px solid #2f2f3d;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.5) !important;
}
.app-select__menu .v-list {
  background: #23232f !important;
  padding: 4px;
}
.app-select__menu .v-list-item {
  min-height: 30px !important;
  border-radius: 6px;
}
.app-select__menu .v-list-item .v-list-item__title {
  font-size: 12px;
  color: #dcdce6;
}
.app-select__menu .v-list-item:hover {
  background: rgba(145, 85, 253, 0.12);
}
.app-select__menu .v-list-item--active {
  background: rgba(145, 85, 253, 0.18) !important;
}
.app-select__menu .v-list-item--active .v-list-item__title {
  color: #b79cff;
}

/* light 메뉴 */
.app-select__menu--light {
  border-color: #e2e0ec;
  box-shadow: 0 8px 28px rgba(80, 60, 120, 0.16) !important;
}
.app-select__menu--light .v-list {
  background: #fff !important;
}
.app-select__menu--light .v-list-item .v-list-item__title {
  color: #37324a;
}
.app-select__menu--light .v-list-item:hover {
  background: rgba(124, 77, 255, 0.06);
}
.app-select__menu--light .v-list-item--active {
  background: rgba(124, 77, 255, 0.1) !important;
}
.app-select__menu--light .v-list-item--active .v-list-item__title {
  color: #7c4dff;
}
</style>
